import * as React from "react";
import {
	Box,
	Grid,
	LinearProgress,
	MenuItem,
	Slider,
	Stack,
	TextField,
	ToggleButtonGroup,
	Tooltip, Typography
} from "@mui/material";
import {
	Chart as ChartJS,
	LinearScale,
	CategoryScale,
	BarElement,
	PointElement,
	LineElement,
	Title,
	Tooltip as ChartTooltip,
	Legend,
	LineController,
	BarController,
	Filler
} from "chart.js";
import { Chart } from "react-chartjs-2";
import {useState} from "react";
import {apiRequest} from "../services/utils/HttpUtils";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {GetNationalResult} from "../graphql/queries_nationalModelResult";
import {getAllLists} from "../graphql/queries_pipelineEditor";
import PlotFilterList from "../components/utils/plotFilterList";
import {
	nationalFilterConfigList,
	nationalResultOptions,
} from "../components/national-state-modelling/nationalModelResultConfig";
import {colorPelletList, fundingList} from "../components/scenario-modelling/TableData";
import * as dayjs from "dayjs";
import formatQueryParams from "../services/utils/formatQueryParams";
import {useMsal} from "@azure/msal-react";
import {caveatIntro} from "../assets/texts/pageTextContent";
import {getUserInfo} from "../graphql/queries_user_info";

ChartJS.register(
	CategoryScale,
	LinearScale,
	BarElement,
	PointElement,
	LineElement,
	Title,
	ChartTooltip,
	Legend,
	LineController,
	BarController,
	Filler
);

// minimum plot range in year
const minRange = 1;

export default function NationalModelResult() {
	const { instance, accounts } = useMsal();
  const client = useQueryClient();
  const {data: userInfo} = getUserInfo();
	const [ plotQueryProps, setPlotQueryProps ] = useState({
    // @ts-ignore
		inJurisdiction: userInfo?.executeGetUser[0].Jurisdiction ??"",
		inInfraFlag: ["ALL"],
		inSource: "ALL",
		inResourceGroupID: [0],
		inSuperSectorID: 0,
		inProjectTypeID: [0],
		inPLEMGroupID: 0,
		inMasterTypeID: 0,
		inLevel1GroupID: 0,
		inLevel2GroupID: 0,
		inFundingMode: "ALL",
		unit: "Value"
	});

	const {data: nationalResult, isFetching} = useQuery(
		["nationalResult", plotQueryProps], {
			queryFn: () => {
				return apiRequest(
					{
						query: GetNationalResult,
						variables: formatQueryParams(plotQueryProps),
						instance: instance,
						accounts: accounts
					});
			},
		}
	);

	const {data: AllLists} = useQuery(
		{
			queryKey: ["Lists"],
			queryFn: () => {
				return apiRequest(
					{
						query: getAllLists,
            // @ts-ignore
						variables: {inJurisdiction: userInfo?.executeGetUser[0].Jurisdiction ??"",
							inScenarioID:""},
						instance: instance,
						accounts: accounts
					});
			},
			cacheTime: Infinity,
      enabled: !!userInfo
		});

	// @ts-ignore
	const Labels = nationalResult?.executeGetNationalModelPlot.map((item) => new Date(item.Month).getFullYear());
	// to be used as x-axis label
	// @ts-ignore
	const formatLabels  = nationalResult?.executeGetNationalModelPlot.map((item) => dayjs(item.Month).format("DD/MM/YYYY"));
	const [plotRange, setPlotRange] =useState<number[]>([
		Labels? Labels[0]: Number(new Date().getFullYear()-10),
		Labels? Labels[Labels.length-1]: Number(new Date().getFullYear()+10)]);

	const plotStart = Labels?.indexOf(plotRange[0]) > 0 ? Labels.indexOf(plotRange[0]): 0;
	const plotEnd = Labels?.lastIndexOf(plotRange[1]) > 0 ? Labels.lastIndexOf(plotRange[1]): Labels?.length;

	// @ts-ignore
	const DetailedData = {
		labels: formatLabels?.slice(plotStart, plotEnd),
		datasets: getNationalDataset(
			// @ts-ignore
			AllLists?.executeGetPLEMTypesList,
			// trim result according to plot range
			// @ts-ignore
			nationalResult?.executeGetNationalModelPlot.slice(
				plotStart,
				plotEnd
			),
			plotQueryProps)
	};

	const maxPlotRange = Labels? Labels[Labels.length-1]: Number(new Date().getFullYear()+10);
	const minPlotRange = Labels? Labels[0]: Number(new Date().getFullYear()-10);

	const handleSliderChange = (
		event: Event,
		newValue: number | number[],
		activeThumb: number,
	) => {
		if (!Array.isArray(newValue)) {
			return;
		}

		if (newValue[1] - newValue[0] < minRange) {
			if (activeThumb === 0) {
				const clamped = Math.min(newValue[0], maxPlotRange - minRange);
				setPlotRange([clamped, clamped + minRange]);
			} else {
				const clamped = Math.max(newValue[1], minRange);
				setPlotRange([clamped - minRange, clamped]);
			}
		} else {
			setPlotRange(newValue as number[]);
		}
	};
	return <Stack>
		<Grid container alignItems={"center"}>
			<Grid item xs={10}>
				{isFetching && <LinearProgress />}
				<Chart type='line' options={nationalResultOptions(plotQueryProps)} data={DetailedData} />
			</Grid>
			<Grid item xs={2}>
				<Grid container spacing={1} columns={1}>
					<PlotFilterList
						AllLists={AllLists}
						plotQueryProps={plotQueryProps}
						setPlotQueryProps={setPlotQueryProps}
						filterConfigList={nationalFilterConfigList}/>
					<Grid item xs={1}>
						<TextField
							key={"fundingMode"}
							label={"Funding mode"}
							name={"inFundingMode"}
							onChange={(e) =>
								setPlotQueryProps({...plotQueryProps, [e.target.name]: e.target.value})
							}
							select
							required
							fullWidth
							value={plotQueryProps["inFundingMode"]}
							variant="filled"
							defaultValue={"ALL"}
							size="small"
						>
							<MenuItem key={"ALL"} value={"ALL"}>
								{"ALL"}
							</MenuItem>
							{fundingList.map((option) => (
								<MenuItem key={option.value} value={option.value}>
									{option.text}
								</MenuItem>
							))}
						</TextField>
					</Grid>
					<Grid item xs={1}>
						<Tooltip title={"Display Unit Filter"}>
							<ToggleButtonGroup
								value={plotQueryProps.unit}
								exclusive
								onChange={(event, value) =>
								{
									if (value != null) {
										setPlotQueryProps({...plotQueryProps, unit: value});
									}
								}
								}
								aria-label="Display Unit Filter"
								fullWidth
								size="small"
							>
								{/*  <ToggleButton value={'value'}>AUD</ToggleButton> */}
								{/*  <ToggleButton value={'unit'}>UNIT</ToggleButton> */}
							</ToggleButtonGroup>
						</Tooltip>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
		<Box sx={{display: "flex", justifyContent: "center", width: "100%", pt: 5}}>
			<Typography id="input-slider" gutterBottom>
                Fiscal Year
			</Typography>
			<Slider
				getAriaLabel={() => "Plot Range"}
				value={plotRange}
				onChange={handleSliderChange}
				valueLabelDisplay="on"
				disableSwap
				step={1}
				marks
				min={minPlotRange}
				max={maxPlotRange}
			/>
		</Box>
		<Box sx={{ fontStyle: "italic", fontWeight: 700, mt: 2, fontFamily: "ProximaNova-Bold" }}>
			<strong>{caveatIntro}</strong>
		</Box>
	</Stack>;
}

export function getNationalDataset (plemList, resultList: Array<any>, plotQueryProps: any) {
	const dataset = {};
	const mode = plotQueryProps.unit;
	plemList?.map((item) => dataset[item.PLEMGroup+mode] = []);

	resultList?.map((item) => {
		plemList?.map((key) => dataset[key.PLEMGroup+mode].push(item[key.PLEMGroup+mode]));
	});
	if (plotQueryProps.inPLEMGroupID == 0) {
		return plemList?.map((item, index) => {
			return {
				type: "line" as const,
				label: item.PLEMGroup,
				data: dataset[item.PLEMGroup+mode]??[],
				fill: true,
				backgroundColor: colorPelletList[index%colorPelletList.length],
			};
		})??[];
	} else {
		const PLEMGroup = plemList.filter((item) => item.PLEMGroupID == plotQueryProps.inPLEMGroupID)[0].PLEMGroup;
        return [{
            type: "line" as const,
            label: PLEMGroup,
            data: dataset[PLEMGroup+mode],
            fill: true,
            backgroundColor: colorPelletList[plotQueryProps.inPLEMGroupID%colorPelletList.length],
        }];
	}

}
